/**
 * Static navigation hints for the HFCL Chat Bot.
 *
 * Each hint maps a list of keywords to a destination page (and optionally a
 * post-navigation click action). The chat does a simple text match — when a
 * user message contains one of the keywords AND has navigation phrasing, the
 * matched hint is offered to the user.
 *
 * To add a new hint:
 *  1. Append a new object with { id, path, label, description, keywords }.
 *  2. Optional: add `postNavAction` (a string) to trigger a click on the
 *     element that has matching `data-chat-action="<value>"` attribute after
 *     the navigation finishes.
 *  3. Optional: add `displayRoute` / `displaySection` to control how the
 *     route is shown back to the user.
 */

export const HFCL_NAVIGATION_HINTS = [
	// ----- Product tour -----
	// Same `stayOnCurrentPath` pattern as the navbar alarm popover —
	// the tour-trigger handler itself navigates to /dashboard and pops
	// the tour modal, so we don't need to push a different route.
	{
		id: 'start-tour',
		path: '/__current__',
		stayOnCurrentPath: true,
		displayRoute: '(current page)',
		label: 'Take a Tour',
		section: 'Navbar',
		description: 'Start the io Canvas product tour from the navbar.',
		keywords: [
			'take a tour', 'start a tour', 'start tour', 'start the tour',
			'take the tour', 'product tour', 'start product tour',
			'open tour', 'open the tour', 'open product tour',
			'show me the tour', 'show me a tour', 'show me product tour',
			'tour guide', 'tour me', 'guide me', 'guided tour',
			'walkthrough', 'walk me through', 'show me around',
			'how do i use this product', 'how does this work',
		],
		postNavAction: 'start-tour',
	},

	// ----- Navbar quick actions -----
	// `stayOnCurrentPath: true` tells performNavigation in useHfclChat to
	// fill in the offer's path with `window.location.pathname` at the time
	// the user accepts the offer (rather than at module-load time). The
	// alarm bell popover works from every page, so we never want to bounce
	// the user off whatever they're looking at.
	{
		id: 'navbar-alarm-summary',
		path: '/__current__',
		stayOnCurrentPath: true,
		displayRoute: '(current page)',
		label: 'Navbar — Alarm Summary',
		section: 'Navbar',
		description: 'Open the alarm summary popover from the navbar bell — Critical / Major / Minor counts plus a peek at the latest active alarms.',
		keywords: [
			// Keywords kept tight to the NAVBAR popover. Avoid bare nouns
			// like "alarms count" / "alarm summary" alone because they
			// can equally describe the Maintenance > Alarms tab.
			'alarm summary popover', 'show alarm summary popover',
			'open alarm summary popover', 'navbar alarms', 'navbar alarm',
			'navbar bell', 'alarm popover', 'alarms popover',
			'alarm bell', 'open alarm bell', 'click the bell',
			'click navbar bell', 'open the bell',
			'critical alarms popover', 'alarm summary from navbar',
			'alarm summary from the navbar bell', 'navbar alarm summary',
		],
		postNavAction: 'navbar-alarm-summary',
	},

	// ----- Entry / authentication pages -----
	{
		id: 'login',
		path: '/signin',
		label: 'Sign In',
		section: 'Auth',
		description: 'Open the Canvas sign-in page so you can enter your username and password.',
		keywords: [
			'login', 'log in', 'sign in', 'signin', 'login page', 'sign in page',
			'open login', 'open sign in', 'take me to login', 'take me to sign in',
			'login form', 'sign in form', 'login screen', 'sign in screen',
			'enter username password', 'enter my username', 'access the application',
			'i need to sign in', 'how do i log in', 'authenticate me',
		],
	},
	{
		id: 'login-otp',
		path: '/signin/otpverify',
		label: 'Sign In — OTP Verification',
		section: 'Auth',
		description: 'Open the two-factor login verification screen where you enter your OTP.',
		keywords: [
			'login otp', 'signin otp', 'sign in otp', 'two factor login', 'two-factor login',
			'2fa login', 'login verification', 'sign in verification', 'verify login',
			'enter login otp', 'login verification code', 'otp screen after login',
			'complete two factor sign in', 'mfa login',
		],
	},
	{
		id: 'signup',
		path: '/signup',
		label: 'Sign Up',
		section: 'Auth',
		description: 'Open the tenant registration page to create a new Canvas account.',
		keywords: [
			'signup', 'sign up', 'register', 'registration', 'create account',
			'new account', 'create a new account', 'register a tenant', 'register tenant',
			'signup form', 'registration form', 'tenant registration', 'open signup',
			'open sign up', 'take me to signup', 'sign up for canvas',
			'create organization account', 'create a new organization',
		],
	},
	{
		id: 'signup-otp',
		path: '/signup/otp/verify',
		label: 'Signup — OTP Verification',
		section: 'Auth',
		description: 'Open the signup email/phone verification screen where you enter the OTP from your email.',
		keywords: [
			'signup otp', 'sign up otp', 'verify signup', 'signup verification',
			'enter signup otp', 'signup verification code', 'email otp', 'phone otp',
			'verify my email', 'verify signup code', 'complete account registration',
			'complete signup', 'signup email verification',
		],
	},
	{
		id: 'reset-password',
		path: '/reset/password',
		label: 'Reset Password',
		section: 'Auth',
		description: 'Open the forgot-password screen to recover access to your account.',
		keywords: [
			'reset password', 'forgot password', 'recover account', 'recover password',
			'password reset', 'forgot my password', 'i forgot my password',
			'cant log in', "can't log in", 'cant sign in', "can't sign in",
			'temporary password', 'request a temporary password',
			'password recovery', 'open forgot password', 'open password reset',
			'take me to forgot password', 'change password because cant log in',
		],
	},
	{
		id: 'license-upload',
		path: '/license/upload',
		label: 'Upload License',
		section: 'License',
		description: 'Open the license upload page to activate or renew your Canvas license.',
		keywords: [
			'upload license', 'license upload', 'activate license', 'license file',
			'license expired', 'expired license', 'submit license', 'submit license.txt',
			'license.txt', 'open license upload', 'take me to license upload',
			'where do i upload license', 'how do i upload license', 'fix expired license',
			'renew license file', 'load license',
		],
	},

	// ----- Canvas Settings (System) -----
	{
		id: 'user-management-add-user',
		path: '/system?tab=user_management',
		label: 'Canvas Settings > User Management (Add User)',
		section: 'Canvas Settings > User Management',
		description:
			'User Management is under Canvas Settings. I can open it and bring up the Add User form for you.',
		keywords: [
			'user management form',
			'add user form',
			'add a user',
			'add new user',
			'create user',
			'create a user',
			'create new user',
			'new user form',
			'open add user',
			'where do i get user management form',
			'where do i get the user management form',
		],
		postNavAction: 'open-user-add',
	},
	{
		id: 'user-management-add-role',
		path: '/system?tab=user_management',
		label: 'Canvas Settings > User Management (Add Role)',
		section: 'Canvas Settings > User Management',
		description:
			'Roles live inside User Management. I can open it and bring up the Add Role form.',
		keywords: [
			'add role',
			'add a role',
			'create role',
			'create a role',
			'new role',
			'add role form',
			'role form',
		],
		postNavAction: 'open-role-add',
	},
	{
		id: 'user-management',
		path: '/system?tab=user_management',
		label: 'Canvas Settings > User Management',
		section: 'Canvas Settings > User Management',
		description: 'Open the User Management screen under Canvas Settings.',
		keywords: [
			'user management',
			'users page',
			'manage users',
			'users tab',
			'users and roles',
		],
	},
	{
		id: 'global-settings',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings',
		section: 'Canvas Settings',
		description: 'Global Settings under Canvas Settings.',
		keywords: ['global settings', 'global setting'],
	},

	// ----- Canvas Settings > Global Settings sections (accordion panels) -----
	// Each one expands the corresponding accordion after navigating, using
	// the imperative setter that globalSettings.jsx exposes on window.
	// The "edit ..." variants chain a second click on the section's Edit
	// pencil so the user lands directly in edit mode.
	{
		id: 'global-canvas-login',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > Canvas Login',
		section: 'Canvas Settings > Global Settings',
		description: 'Canvas Login section under Global Settings.',
		keywords: [
			'canvas login', 'login settings', 'user authentication validity',
			'suspension user time', 'login section',
		],
		postNavAction: 'set-global-section:canvas-login',
	},
	{
		id: 'global-canvas-login-edit',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > Canvas Login (Edit)',
		section: 'Canvas Settings > Global Settings',
		description: 'Open Canvas Login and put it in edit mode.',
		keywords: [
			'edit canvas login', 'change login settings', 'update canvas login',
			'edit user authentication', 'edit suspension time',
		],
		postNavAction: ['set-global-section:canvas-login', 'edit-section-canvas-login'],
	},
	{
		id: 'global-access-point-settings',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > Access Point Settings',
		section: 'Canvas Settings > Global Settings',
		description: 'Access Point Settings section under Global Settings.',
		keywords: [
			'access point settings', 'ap settings', 'system settings',
			'access point config',
		],
		postNavAction: 'set-global-section:system',
	},
	{
		id: 'global-access-point-settings-edit',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > Access Point Settings (Edit)',
		section: 'Canvas Settings > Global Settings',
		description: 'Open Access Point Settings in edit mode.',
		keywords: ['edit access point settings', 'edit ap settings', 'update access point settings'],
		postNavAction: ['set-global-section:system', 'edit-section-system'],
	},
	{
		id: 'global-tr069',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > TR-069',
		section: 'Canvas Settings > Global Settings',
		description: 'TR-069 section under Global Settings.',
		keywords: ['tr-069', 'tr069', 'tr 069', 'cwmp'],
		postNavAction: 'set-global-section:tr069',
	},
	{
		id: 'global-tr069-edit',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > TR-069 (Edit)',
		section: 'Canvas Settings > Global Settings',
		description: 'Open TR-069 in edit mode.',
		keywords: ['edit tr-069', 'edit tr069', 'update tr-069'],
		postNavAction: ['set-global-section:tr069', 'edit-section-tr069'],
	},
	{
		id: 'global-nbi',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > NBI',
		section: 'Canvas Settings > Global Settings',
		description: 'NBI section under Global Settings.',
		keywords: ['nbi', 'northbound interface', 'nbi settings'],
		postNavAction: 'set-global-section:nbi',
	},
	{
		id: 'global-nbi-edit',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > NBI (Edit)',
		section: 'Canvas Settings > Global Settings',
		description: 'Open NBI in edit mode.',
		keywords: ['edit nbi', 'update nbi'],
		postNavAction: ['set-global-section:nbi', 'edit-section-nbi'],
	},
	{
		id: 'global-ntp',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > NTP',
		section: 'Canvas Settings > Global Settings',
		description: 'NTP section under Global Settings.',
		keywords: ['ntp', 'ntp settings', 'ntp server', 'time server', 'network time'],
		postNavAction: 'set-global-section:ntp',
	},
	{
		id: 'global-ntp-edit',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > NTP (Edit)',
		section: 'Canvas Settings > Global Settings',
		description: 'Open NTP in edit mode.',
		keywords: ['edit ntp', 'edit ntp server', 'update ntp', 'change ntp'],
		postNavAction: ['set-global-section:ntp', 'edit-section-ntp'],
	},
	{
		id: 'global-smtp',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > SMTP',
		section: 'Canvas Settings > Global Settings',
		description: 'SMTP section under Global Settings.',
		keywords: ['smtp', 'smtp server', 'mail server', 'email server'],
		postNavAction: 'set-global-section:smtp',
	},
	{
		id: 'global-smtp-edit',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > SMTP (Edit)',
		section: 'Canvas Settings > Global Settings',
		description: 'Open SMTP in edit mode.',
		keywords: ['edit smtp', 'change smtp', 'update smtp server'],
		postNavAction: ['set-global-section:smtp', 'edit-section-smtp'],
	},
	{
		id: 'global-syslog',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > Syslog',
		section: 'Canvas Settings > Global Settings',
		description: 'Syslog section under Global Settings.',
		keywords: ['syslog', 'syslog server', 'remote logging'],
		postNavAction: 'set-global-section:syslog',
	},
	{
		id: 'global-syslog-edit',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > Syslog (Edit)',
		section: 'Canvas Settings > Global Settings',
		description: 'Open Syslog in edit mode.',
		keywords: ['edit syslog', 'change syslog', 'update syslog'],
		postNavAction: ['set-global-section:syslog', 'edit-section-syslog'],
	},
	{
		id: 'global-external-auth',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > External Authentication',
		section: 'Canvas Settings > Global Settings',
		description: 'External Authentication section (RADIUS / LDAP / TACACS).',
		keywords: ['external authentication', 'external auth', 'radius', 'ldap', 'tacacs', 'sso'],
		postNavAction: 'set-global-section:external-auth',
	},
	{
		id: 'global-external-auth-edit',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > External Authentication (Edit)',
		section: 'Canvas Settings > Global Settings',
		description: 'Open External Authentication in edit mode.',
		keywords: ['edit external authentication', 'edit external auth', 'edit radius', 'edit ldap'],
		postNavAction: ['set-global-section:external-auth', 'edit-section-external-auth'],
	},
	{
		id: 'global-portal-customization',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > Canvas Portal Customization',
		section: 'Canvas Settings > Global Settings',
		description: 'Canvas Portal Customization section (logo, colours, branding).',
		keywords: [
			'canvas portal customization', 'portal customization', 'customization',
			'branding', 'logo', 'theme', 'portal branding',
		],
		postNavAction: 'set-global-section:customisation',
	},
	{
		id: 'global-portal-customization-edit',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > Canvas Portal Customization (Edit)',
		section: 'Canvas Settings > Global Settings',
		description: 'Open Canvas Portal Customization in edit mode.',
		keywords: ['edit portal customization', 'edit branding', 'change logo', 'change theme'],
		postNavAction: ['set-global-section:customisation', 'edit-section-customisation'],
	},
	{
		id: 'global-custom-ui-labels',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > Custom UI Labels',
		section: 'Canvas Settings > Global Settings',
		description: 'Custom UI Labels section (rename dashboard labels).',
		keywords: ['custom ui labels', 'ui labels', 'custom labels', 'rename labels'],
		postNavAction: 'set-global-section:customui-labels',
	},
	{
		id: 'global-custom-ui-labels-edit',
		path: '/system?tab=global_settings',
		label: 'Canvas Settings > Global Settings > Custom UI Labels (Edit)',
		section: 'Canvas Settings > Global Settings',
		description: 'Open Custom UI Labels in edit mode.',
		keywords: ['edit custom ui labels', 'edit ui labels', 'rename labels'],
		postNavAction: ['set-global-section:customui-labels', 'edit-section-customui-labels'],
	},
	{
		id: 'license-management',
		path: '/system?tab=license_management',
		label: 'Canvas Settings > License Management',
		section: 'Canvas Settings',
		description: 'License Management tab inside Canvas Settings.',
		keywords: ['license', 'licenses', 'license management'],
	},
	{
		id: 'security',
		path: '/system?tab=security',
		label: 'Canvas Settings > Security',
		section: 'Canvas Settings',
		description: 'Security tab inside Canvas Settings (WIDS / WIPS).',
		keywords: ['security tab', 'wids', 'wips', 'security settings'],
	},
	{
		id: 'system',
		path: '/system',
		label: 'Canvas Settings',
		section: 'Canvas Settings',
		description: 'Open Canvas Settings from the sidebar.',
		keywords: ['canvas settings', 'system settings'],
	},

	// ----- Maintenance -----
	// NOTE — Maintenance.jsx does NOT read ?tab= from the URL. Tab switching
	// happens via setSelectedMenu in Maintenance.jsx. So for each tab here we
	// navigate to /Maintenance and then ask the chat to click the tab Box on
	// the destination page (every tab has data-chat-action="maintenance-tab-<id>").
	{
		id: 'maintenance-reports',
		path: '/Maintenance',
		label: 'Maintenance > Reports',
		section: 'Maintenance',
		description: 'Reports are under the Maintenance section.',
		keywords: [
			'report', 'reports', 'kpi report', 'kpi reports',
			'generate report', 'generate kpi report', 'generate reports',
			'how to generate kpi report', 'how to generate report',
			'network report', 'network reports', 'run report', 'run a report',
			'how to generate ap connected client report',
			'ap connected client report', 'connected client report',
			'sensor mode report', 'wids report', 'wids reports', 'sensor mode',
			'report section', 'reports section', 'reports tab',
			'maintenance reports', 'scheduled report', 'scheduled reports',
			'edit report', 'edit reports', 'edit an existing report',
		],
		postNavAction: 'maintenance-tab-reports',
	},
	{
		id: 'maintenance-reports-custom',
		path: '/Maintenance',
		label: 'Maintenance > Reports (Create Custom Report)',
		section: 'Maintenance > Reports',
		description:
			'Custom Reports live inside Maintenance > Reports. I can open the Reports tab and bring up the Create Custom Report form for you.',
		keywords: [
			'custom report', 'custom reports', 'custom report form',
			'custom reports form', 'create custom report', 'create custom reports',
			'new custom report', 'add custom report', 'open custom report',
		],
		postNavAction: ['maintenance-tab-reports', 'open-custom-report'],
	},
	{
		id: 'maintenance-reports-predefined',
		path: '/Maintenance',
		label: 'Maintenance > Reports (Pre-defined Reports)',
		section: 'Maintenance > Reports',
		description:
			'Pre-defined Reports live inside Maintenance > Reports. I can open the Reports tab and bring up the Pre-defined Reports flow for you.',
		keywords: [
			'predefined report', 'predefined reports', 'pre-defined report',
			'pre-defined reports', 'pre defined report', 'pre defined reports',
			'create predefined report', 'open predefined report',
		],
		postNavAction: ['maintenance-tab-reports', 'open-predefined-report'],
	},
	{
		id: 'maintenance-alarms',
		path: '/Maintenance',
		label: 'Maintenance > Alarms',
		section: 'Maintenance',
		description: 'Alarms are under Maintenance > Alarms.',
		keywords: [
			'alarm', 'alarms', 'alarms tab',
			'historical alarm', 'historical alarms', 'alarm history',
			'active alarm', 'active alarms', 'active network alarms',
			'critical alarm', 'critical alarms', 'critical alerts', 'critical alert',
			'show me critical alarms', 'show critical alarms',
			'switch alarm', 'switch alarms', 'where are alarms',
			'alarm summary', 'alarms summary',
			'maintenance alarms', 'open alarms', 'show alarms', 'view alarms',
		],
		postNavAction: 'maintenance-tab-alarms',
	},
	{
		id: 'maintenance-logs',
		path: '/Maintenance',
		label: 'Maintenance > Logs',
		section: 'Maintenance',
		description: 'Logs are under Maintenance > Logs.',
		keywords: [
			'log', 'logs', 'logs tab',
			'audit log', 'audit logs', 'filter audit logs',
			'syslog', 'syslog log', 'system logs', 'system log',
			'ap snapshot history', 'snapshot history',
			'log entry', 'log entries', 'view log entry', 'who changed what',
			'maintenance logs', 'open logs', 'show logs', 'view logs',
			'i want to navigate to syslog', 'navigate to syslog', 'go to syslog',
		],
		postNavAction: 'maintenance-tab-logs',
	},
	{
		id: 'maintenance-system-health',
		path: '/Maintenance',
		label: 'Maintenance > System Health',
		section: 'Maintenance',
		description: 'System Health (Infra) is under Maintenance.',
		keywords: [
			'system health', 'system health infra', 'infra health',
			'system health tab', 'health infra',
		],
		postNavAction: 'maintenance-tab-systemHealth',
	},
	{
		id: 'maintenance-backup',
		path: '/Maintenance',
		label: 'Maintenance > Backup',
		section: 'Maintenance',
		description: 'Backup is under Maintenance > Backup.',
		keywords: [
			'backup', 'backups', 'backup tab',
			'take backup', 'create backup', 'create a backup', 'create system backup',
			'restore backup', 'restore', 'restore from backup',
			'site backup', 'site backups', 'back up site', 'back up a site',
			'how do i back up a site', 'where are site backups',
			'maintenance backup', 'open backup', 'show backups',
		],
		postNavAction: 'maintenance-tab-backup',
	},
	{
		id: 'firmware-management',
		path: '/Maintenance',
		label: 'Maintenance > Firmware Management',
		section: 'Maintenance',
		description: 'Firmware Management is under Maintenance.',
		keywords: [
			// This intent OPENS the tab. It handles:
			//   (a) broad "firmware" / "firmware management" queries,
			//   (b) informational "where can I find …" / "where is …" phrasing,
			//   (c) upgrade/update noun phrases like "firmware upgrade" —
			//       those are AMBIGUOUS (page navigation vs. add-modal),
			//       so we default to the page. The user can then click
			//       + Add Firmware or ask "add firmware" for the modal.
			'firmware', 'firmware management', 'firmware tab',
			'firmware page', 'firmware section', 'firmware screen',
			'firmware upgrade', 'firmware update',
			'upgrade firmware', 'update firmware',
			'upgrade to latest firmware',
			'where do i update firmware', 'where do i upgrade firmware',
			'where do i update firmware in this',
			'where can i find firmware', 'where can i find firmware upgrade',
			'where can i find firmware update',
			'where is firmware', 'where is firmware upgrade',
			'find firmware', 'find firmware upgrade',
			'manage firmware', 'manage ap firmware', 'manage switch firmware',
			'push firmware', 'schedule firmware', 'schedule firmware upgrade',
			'switch firmware versions',
			'ap auto upgrade', 'auto upgrade',
			'upgrade devices',
		],
		postNavAction: 'maintenance-tab-firmware_management',
	},
	{
		// Specific "I want to ADD a firmware image" intent — triggers the
		// declarative flow (see chat-backend/flows/firmware-add.json).
		// Keywords are intentionally limited to unambiguous create-verbs
		// (add / upload / create / new) so informational queries never
		// accidentally open the modal.
		id: 'firmware-add',
		path: '/Maintenance',
		label: 'Maintenance > Firmware Management (+ Add Firmware)',
		section: 'Maintenance',
		description: 'Open the Add Firmware form under Firmware Management.',
		keywords: [
			'add firmware', 'add a firmware', 'add new firmware',
			'add ap firmware', 'add access point firmware',
			'upload firmware', 'upload ap firmware',
			'upload firmware image', 'upload new firmware',
			'upload firmware file',
			'how to add firmware', 'how do i add firmware',
			'how can i add firmware',
			'how to upload firmware', 'how do i upload firmware',
			'create firmware', 'create a firmware', 'create new firmware',
			'new firmware',
			'add firmware image', 'add firmware file',
			'add a new firmware image',
		],
		// Chain: open the tab → expand the AP Firmware accordion (the
		// "+ Add Firmware" button is only rendered while the accordion is
		// open) → click the "+ Add Firmware" button.
		postNavAction: [
			'maintenance-tab-firmware_management',
			'expand-widget:Access Point Firmwares',
			'firmware-add',
		],
	},
	{
		// Distinct short-form for the Switch Firmwares accordion — same
		// architecture, different DOM target.
		id: 'firmware-add-switch',
		path: '/Maintenance',
		label: 'Maintenance > Firmware Management (+ Add Switch Firmware)',
		section: 'Maintenance',
		description: 'Open the Add Switch Firmware form under Firmware Management.',
		keywords: [
			'add switch firmware', 'add a switch firmware', 'add new switch firmware',
			'upload switch firmware', 'how to add switch firmware',
			'how do i add switch firmware', 'switch firmware add',
		],
		postNavAction: [
			'maintenance-tab-firmware_management',
			'expand-widget:Switch Firmwares',
			'firmware-add-switch',
		],
	},
	{
		// Dedicated intent for the "Access Point Firmware Scheduled Tasks"
		// accordion. Opening the Firmware Management tab alone isn't enough
		// — we also need to open the specific accordion so the user lands
		// exactly on the scheduled-tasks table. Used both as a chip target
		// from the firmware-add flow and as a direct query.
		id: 'firmware-scheduled-tasks',
		path: '/Maintenance',
		label: 'Maintenance > Firmware Management > Scheduled Tasks',
		section: 'Maintenance',
		description: 'Open the Access Point Firmware Scheduled Tasks accordion.',
		keywords: [
			'firmware scheduled tasks', 'open firmware scheduled tasks',
			'view firmware scheduled tasks', 'show firmware scheduled tasks',
			'scheduled firmware tasks', 'firmware jobs',
			'firmware upgrade tasks', 'firmware upgrade task',
			'view scheduled tasks', 'show scheduled tasks',
			'scheduled tasks', 'open scheduled tasks',
			'ap firmware scheduled tasks', 'access point firmware scheduled tasks',
		],
		postNavAction: [
			'maintenance-tab-firmware_management',
			'expand-widget:Access Point Firmware Scheduled Tasks',
		],
	},
	{
		id: 'bulk-operations',
		path: '/Maintenance',
		label: 'Maintenance > Bulk Operations',
		section: 'Maintenance',
		description: 'Bulk Operations are under Maintenance.',
		keywords: [
			// Bare / generic — these intentionally don't pin to a specific
			// card. Use the more specific intents below ("bulk add sites",
			// "bulk delete switches", etc.) to open the right form.
			'bulk operation', 'bulk operations', 'bulk',
			'bulk operations tab', 'bulk update', 'run bulk actions',
			'bulk config audit', 'config audit',
		],
		postNavAction: 'maintenance-tab-bulk_operations',
	},
	// ----- Specific Bulk Operations cards -----
	// Each one chains: open Maintenance → click the Bulk Operations tab →
	// click the corresponding card (tagged via data-chat-action on the
	// Grid item). The card click is what reveals the inline form/screen
	// for that operation.
	{
		id: 'bulk-add-sites',
		path: '/Maintenance',
		label: 'Maintenance > Bulk Operations (Bulk Add Sites)',
		section: 'Maintenance > Bulk Operations',
		description: 'Open the Bulk Addition flow for Sites — upload a CSV to add many sites at once.',
		keywords: [
			'bulk add sites', 'bulk upload sites', 'bulk add site', 'bulk upload site',
			'add multiple sites', 'upload multiple sites', 'upload sites csv',
			'add sites csv', 'where to upload bulk sites', 'where to add bulk sites',
			'add many sites', 'where do i bulk add sites', 'mass add sites',
			'how to bulk add sites', 'how to upload sites in bulk',
		],
		postNavAction: ['maintenance-tab-bulk_operations', 'bulk-add-sites'],
	},
	{
		id: 'bulk-add-aps',
		path: '/Maintenance',
		label: 'Maintenance > Bulk Operations (Bulk Add Access Points)',
		section: 'Maintenance > Bulk Operations',
		description: 'Open the Bulk Addition flow for Access Points — upload a CSV to add many APs at once.',
		keywords: [
			'bulk add access points', 'bulk add aps', 'bulk add ap',
			'bulk upload access points', 'bulk upload aps',
			'add multiple access points', 'add multiple aps',
			'upload access points csv', 'upload aps csv',
			'where to upload bulk aps', 'where to add bulk aps',
			'mass add access points', 'mass add aps',
			'how to bulk add access points', 'how to upload aps in bulk',
		],
		postNavAction: ['maintenance-tab-bulk_operations', 'bulk-add-aps'],
	},
	{
		id: 'bulk-add-switches',
		path: '/Maintenance',
		label: 'Maintenance > Bulk Operations (Bulk Add Switches)',
		section: 'Maintenance > Bulk Operations',
		description: 'Open the Bulk Addition flow for Switches — upload a CSV to add many switches at once.',
		keywords: [
			'bulk add switches', 'bulk add switch', 'bulk upload switches',
			'add multiple switches', 'upload switches csv',
			'where to upload bulk switches', 'where to add bulk switches',
			'mass add switches', 'how to bulk add switches',
		],
		postNavAction: ['maintenance-tab-bulk_operations', 'bulk-add-switches'],
	},
	{
		id: 'bulk-delete-sites',
		path: '/Maintenance',
		label: 'Maintenance > Bulk Operations (Bulk Delete Sites)',
		section: 'Maintenance > Bulk Operations',
		description: 'Open the Bulk Deletion flow for Sites.',
		keywords: [
			'bulk delete sites', 'bulk delete site', 'bulk remove sites',
			'delete multiple sites', 'remove multiple sites',
			'mass delete sites', 'how to bulk delete sites',
			'where do i bulk delete sites',
		],
		postNavAction: ['maintenance-tab-bulk_operations', 'bulk-delete-sites'],
	},
	{
		id: 'bulk-delete-aps',
		path: '/Maintenance',
		label: 'Maintenance > Bulk Operations (Bulk Delete Access Points)',
		section: 'Maintenance > Bulk Operations',
		description: 'Open the Bulk Deletion flow for Access Points.',
		keywords: [
			'bulk delete access points', 'bulk delete aps', 'bulk delete ap',
			'bulk remove access points', 'bulk remove aps',
			'delete multiple access points', 'delete multiple aps',
			'mass delete access points', 'mass delete aps',
			'how to bulk delete aps', 'where do i bulk delete aps',
		],
		postNavAction: ['maintenance-tab-bulk_operations', 'bulk-delete-aps'],
	},
	{
		id: 'bulk-delete-switches',
		path: '/Maintenance',
		label: 'Maintenance > Bulk Operations (Bulk Delete Switches)',
		section: 'Maintenance > Bulk Operations',
		description: 'Open the Bulk Deletion flow for Switches.',
		keywords: [
			'bulk delete switches', 'bulk delete switch', 'bulk remove switches',
			'delete multiple switches', 'mass delete switches',
			'how to bulk delete switches',
		],
		postNavAction: ['maintenance-tab-bulk_operations', 'bulk-delete-switches'],
	},
	{
		id: 'bulk-action-aps',
		path: '/Maintenance',
		label: 'Maintenance > Bulk Operations (Bulk Reboot Access Points)',
		section: 'Maintenance > Bulk Operations',
		description: 'Open the Bulk Reboot / Reset action modal for Access Points.',
		keywords: [
			'bulk reboot access points', 'bulk reboot aps', 'bulk reboot ap',
			'bulk reset access points', 'bulk reset aps',
			'reboot multiple access points', 'reboot multiple aps',
			'reset multiple access points', 'mass reboot aps',
			'bulk action access points', 'bulk action aps',
			'bulk maintenance access points', 'bulk maintenance aps',
		],
		postNavAction: ['maintenance-tab-bulk_operations', 'bulk-action-aps'],
	},
	{
		id: 'bulk-action-switches',
		path: '/Maintenance',
		label: 'Maintenance > Bulk Operations (Bulk Reboot Switches)',
		section: 'Maintenance > Bulk Operations',
		description: 'Open the Bulk Reboot / Reset action modal for Switches.',
		keywords: [
			'bulk reboot switches', 'bulk reboot switch',
			'bulk reset switches', 'reboot multiple switches',
			'reset multiple switches', 'mass reboot switches',
			'bulk action switches', 'bulk action switch',
			'bulk maintenance switches',
		],
		postNavAction: ['maintenance-tab-bulk_operations', 'bulk-action-switches'],
	},
	{
		id: 'network-health-center',
		path: '/Maintenance',
		label: 'Maintenance > Network Health Center',
		section: 'Maintenance',
		description: 'Network Health Center is under Maintenance.',
		keywords: [
			'network health center', 'network health centre', 'nhc',
			'network health', 'health center', 'health centre',
			'which sites have issues', 'sites with issues',
			'affected access points', 'affected aps', 'show affected aps',
			'clients with issues', 'clients with connectivity issues',
			'connectivity issues', 'issues summary', 'view issues summary',
			'problematic site', 'problem sites',
		],
		postNavAction: 'maintenance-tab-network_health_center',
	},
	{
		id: 'maintenance-troubleshooting',
		path: '/Maintenance',
		label: 'Maintenance > Troubleshooting',
		section: 'Maintenance',
		description: 'Troubleshooting is under Maintenance.',
		keywords: [
			'troubleshoot', 'troubleshooting', 'troubleshoot tab',
			'diagnose', 'diagnosis', 'diagnostics',
			'troubleshooting tools', 'admin troubleshooting',
			'troubleshooting utilities', 'admin utilities',
		],
		postNavAction: 'maintenance-tab-troubleshooting',
	},
	{
		id: 'maintenance',
		path: '/Maintenance',
		label: 'Maintenance',
		section: 'Maintenance',
		description: 'Open Maintenance from the sidebar.',
		keywords: [
			'maintenance', 'maintenance page', 'maintenance section',
			'system health', 'troubleshoot', 'troubleshooting',
		],
	},

	// ----- Profile / account -----
	{
		id: 'account-details',
		path: '/profile',
		label: 'Profile > Account Details',
		section: 'Profile',
		description: 'Open Account Details.',
		keywords: [
			'account details', 'account detail', 'profile', 'my profile',
			'my account', 'open my profile', 'open profile', 'take me to my profile',
			'update personal information', 'update my information',
			'edit my name', 'edit my email', 'edit name and email',
			'manage my account', 'where can i manage my account',
			'complete my profile', 'first time login', 'logging in for the first time',
		],
	},
	{
		id: 'manage-password',
		path: '/manage-password',
		label: 'Profile > Password & 2FA',
		section: 'Profile',
		description: 'Open Password & 2FA settings.',
		keywords: [
			'change password', 'change my password', 'update password',
			'update my password', 'manage password', 'password and 2fa',
			'password & 2fa', 'password 2fa settings', 'update password and 2fa',
			'2fa', 'two factor', 'two-factor', 'two factor authentication',
			'two-factor authentication', 'enable 2fa', 'enable two factor',
			'setup 2fa', 'set up 2fa', 'set up two factor', 'configure 2fa',
			'manage two factor', 'manage two-factor', 'manage two factor auth',
			'where do i manage two factor', 'where do i manage 2fa',
		],
	},

	// ----- Sidebar pages -----
	{
		id: 'templates',
		path: '/template',
		label: 'Templates',
		section: 'Templates',
		description: 'Open Templates / Global Profiles from the sidebar.',
		keywords: [
			'template', 'templates', 'open templates', 'show templates',
			'global profile', 'global profiles', 'open global profiles',
			'show me global profiles', 'reusable network profiles',
			'reusable profiles', 'profiles', 'network profiles',
			'add a new global profile', 'new global profile',
		],
	},
	// ----- Template page accordions -----
	// expand-widget locates the accordion by its visible title text and
	// clicks the summary, scrolling it into view.
	{
		id: 'templates-mac-acl',
		path: '/template',
		label: 'Templates > Access Controls (MAC ACL)',
		section: 'Templates',
		description: 'Open the Access Controls (MAC ACL) accordion under Templates.',
		keywords: [
			'mac acl', 'add mac acl', 'add a mac acl', 'open mac acl',
			'access controls', 'media access control', 'access control list',
			'open access controls',
		],
		postNavAction: 'expand-widget:Access Controls',
	},
	{
		id: 'templates-rbac',
		path: '/template',
		label: 'Templates > Role-Based Access',
		section: 'Templates',
		description: 'Open the Role-Based Access accordion under Templates.',
		keywords: [
			'role based access', 'role-based access', 'rbac',
			'open role based access', 'role profile', 'role profiles',
			'dynamic authorization',
		],
		postNavAction: 'expand-widget:Role-Based Access',
	},
	{
		id: 'templates-vlan-pool',
		path: '/template',
		label: 'Templates > VLAN Pool',
		section: 'Templates',
		description: 'Open the VLAN Pool accordion under Templates.',
		keywords: [
			'vlan pool', 'create vlan pool', 'add vlan pool', 'open vlan pool',
			'virtual lan pool', 'vlan pooling',
		],
		postNavAction: 'expand-widget:VLAN Pool',
	},
	{
		id: 'templates-acs-schedule',
		path: '/template',
		label: 'Templates > ACS Schedule',
		section: 'Templates',
		description: 'Open the ACS Schedule accordion under Templates.',
		keywords: [
			'acs scheduler', 'acs schedule', 'add acs scheduler', 'add acs schedule',
			'open acs schedule', 'automatic channel selection schedule',
			'automatic channel selection',
		],
		postNavAction: 'expand-widget:ACS Schedule',
	},
	{
		id: 'templates-ssid-schedule',
		path: '/template',
		label: 'Templates > SSID Schedule',
		section: 'Templates',
		description: 'Open the SSID Schedule accordion under Templates.',
		keywords: [
			'ssid schedule', 'create ssid schedule', 'add ssid schedule',
			'open ssid schedule', 'service set identifier schedule',
		],
		postNavAction: 'expand-widget:SSID Schedule',
	},
	{
		id: 'templates-ssid-template',
		path: '/template',
		label: 'Templates > SSID Template',
		section: 'Templates',
		description: 'Open the SSID Template accordion under Templates.',
		keywords: [
			'ssid template', 'add ssid template', 'open ssid template',
			'create ssid template',
		],
		postNavAction: 'expand-widget:SSID Template',
	},
	{
		id: 'templates-atf',
		path: '/template',
		label: 'Templates > ATF Profile',
		section: 'Templates',
		description: 'Open the ATF (Air Time Fairness) profile accordion.',
		keywords: [
			'atf profile', 'air time fairness', 'air-time fairness',
			'configure atf profile', 'open atf', 'atf',
		],
		postNavAction: 'expand-widget:ATF',
	},
	{
		id: 'templates-eogre',
		path: '/template',
		label: 'Templates > EoGRE Profile',
		section: 'Templates',
		description: 'Open the EoGRE profile accordion.',
		keywords: [
			'eogre profile', 'add eogre profile', 'open eogre', 'eogre',
		],
		postNavAction: 'expand-widget:EoGRE',
	},
	{
		id: 'templates-nat',
		path: '/template',
		label: 'Templates > NAT Profile',
		section: 'Templates',
		description: 'Open the NAT profile accordion.',
		keywords: [
			'nat profile', 'create nat profile', 'open nat profile', 'nat',
			'network address translation',
		],
		postNavAction: 'expand-widget:NAT',
	},
	{
		id: 'templates-multi-psk',
		path: '/template',
		label: 'Templates > Multi-PSK',
		section: 'Templates',
		description: 'Open the Multi-PSK accordion.',
		keywords: [
			'multi psk', 'multi-psk', 'set up multi-psk', 'open multi psk',
			'multiple psk',
		],
		postNavAction: 'expand-widget:Multi-PSK',
	},
	{
		id: 'templates-aaa',
		path: '/template',
		label: 'Templates > AAA Server',
		section: 'Templates',
		description: 'Open the AAA Server accordion.',
		keywords: [
			'aaa server', 'add aaa server', 'add an aaa server', 'open aaa server',
			'radius server', 'tacacs server',
		],
		postNavAction: 'expand-widget:AAA',
	},
	{
		id: 'templates-snmp',
		path: '/template',
		label: 'Templates > SNMP Profile',
		section: 'Templates',
		description: 'Open the SNMP Profile accordion.',
		keywords: [
			'snmp profile', 'snmp profiles', 'configure snmp profiles', 'snmp',
			'open snmp', 'snmp settings',
		],
		postNavAction: 'expand-widget:SNMP',
	},
	{
		id: 'iopass',
		path: '/iopass',
		label: 'io Pass Captive Portal',
		section: 'io Pass Captive Portal',
		description: 'Open io Pass Captive Portal.',
		keywords: [
			'iopass', 'io pass', 'open io pass', 'take me to io pass',
			'captive portal', 'captive portal settings', 'open captive portal',
			'guest wifi', 'guest wi-fi', 'guest wifi settings', 'guest wifi plans',
			'configure guest wifi', 'where do i configure guest wifi',
			'guest access', 'guest access plan', 'add a guest access plan',
			'edit captive portal profile', 'set up captive portals',
			'guest client sessions', 'guest sessions', 'guest portal',
			'io pass session history', 'io pass sessions',
			'who is connected through captive portal',
		],
	},
	{
		id: 'topology',
		path: '/topology',
		label: 'Topology / Site Dashboard',
		section: 'Topology',
		description: 'Open Topology — the site hierarchy / site dashboard.',
		keywords: [
			'topology', 'open topology', 'show topology', 'topology view',
			'site hierarchy', 'site tree', 'site dashboard', 'open site dashboard',
			'site topology', 'browse sites', 'browse my sites', 'site browse',
			'all locations', 'my locations', 'see all locations',
			'navigate to a floor', 'navigate to a building',
			'site dashboard for my location', 'network management for a site',
			'access points at a site', 'switches at this site',
			'switch between access points and switches tabs',
			'toggle local view', 'toggle aggregated view', 'local vs aggregated view',
			'site hierarchy panel', 'show site hierarchy', 'hide site hierarchy',
			'show me sites', 'sites at locations',
		],
	},
	{
		id: 'dashboard',
		path: '/Dashboard',
		label: 'Home Dashboard',
		section: 'Home Dashboard',
		description: 'Open Home Dashboard from the sidebar.',
		keywords: ['dashboard', 'home dashboard', 'home page'],
	},
	{
		id: 'dashboard-download-pdf',
		path: '/Dashboard',
		label: 'Home Dashboard (Download PDF Report)',
		section: 'Home Dashboard',
		description:
			'Generate and download the Home Dashboard as a PDF report.',
		keywords: [
			'download dashboard report', 'download dashboard pdf',
			'download dashboard', 'export dashboard', 'export dashboard pdf',
			'save dashboard as pdf', 'dashboard report pdf',
			'generate dashboard report', 'pdf report',
		],
		postNavAction: 'download-dashboard-report',
	},
	{
		id: 'dashboard-refresh',
		path: '/Dashboard',
		label: 'Home Dashboard (Refresh)',
		section: 'Home Dashboard',
		description: 'Refresh the Home Dashboard data.',
		keywords: [
			'refresh dashboard', 'reload dashboard', 'refresh home dashboard',
			'reload home dashboard', 'update dashboard',
		],
		postNavAction: 'refresh-dashboard',
	},

	// ----- Home Dashboard graphs / widgets -----
	// Navigates to the Home Dashboard (if not already there), then calls
	// scroll-to-widget with the widget's visible title. The widget is
	// scrolled into view and given a 2-second blue pulse highlight.
	{
		id: 'dash-clients',
		path: '/Dashboard',
		label: 'Home Dashboard > Clients',
		section: 'Home Dashboard',
		description: 'Open the Clients graph on Home Dashboard.',
		keywords: ['clients graph', 'connected clients graph', 'clients widget'],
		postNavAction: 'expand-widget:Clients',
	},
	{
		id: 'dash-active-qoe',
		path: '/Dashboard',
		label: 'Home Dashboard > Active QOE',
		section: 'Home Dashboard',
		description: 'Open the Active QOE graph on Home Dashboard.',
		keywords: ['active qoe', 'qoe graph', 'qoe widget', 'active quality of experience'],
		postNavAction: 'expand-widget:Active QOE',
	},
	{
		id: 'dash-issues',
		path: '/Dashboard',
		label: 'Home Dashboard > Issues',
		section: 'Home Dashboard',
		description: 'Open the Issues graph on Home Dashboard.',
		keywords: ['issues graph', 'issues widget', 'network issues'],
		postNavAction: 'expand-widget:Issues',
	},
	{
		id: 'dash-top-performing',
		path: '/Dashboard',
		label: 'Home Dashboard > Top Performing Sites',
		section: 'Home Dashboard',
		description: 'Open the Top Performing Sites graph.',
		keywords: ['top performing sites', 'top performing', 'best performing sites'],
		postNavAction: 'expand-widget:Top Performing Sites',
	},
	{
		id: 'dash-top-impacted',
		path: '/Dashboard',
		label: 'Home Dashboard > Top Impacted Sites',
		section: 'Home Dashboard',
		description: 'Open the Top Impacted Sites graph.',
		keywords: ['top impacted sites', 'top impacted', 'worst performing sites'],
		postNavAction: 'expand-widget:Top Impacted Sites',
	},
	{
		id: 'dash-client-models',
		path: '/Dashboard',
		label: 'Home Dashboard > Client Models',
		section: 'Home Dashboard',
		description: 'Open the Client Models graph.',
		keywords: ['client models', 'client model', 'device models'],
		postNavAction: 'expand-widget:Client Models',
	},
	{
		id: 'dash-wifi-protocol',
		path: '/Dashboard',
		label: 'Home Dashboard > Wi-Fi Protocol Distribution',
		section: 'Home Dashboard',
		description: 'Open the Wi-Fi Protocol Distribution graph.',
		keywords: [
			'wifi protocol distribution', 'wi-fi protocol distribution',
			'wifi protocol', 'protocol distribution', 'wifi 6', 'wifi 5',
		],
		postNavAction: 'expand-widget:Wi-Fi Protocol Distribution',
	},
	{
		id: 'dash-sites-highest-data',
		path: '/Dashboard',
		label: 'Home Dashboard > Sites with Highest Data Usage',
		section: 'Home Dashboard',
		description: 'Open the Sites with Highest Data Usage graph.',
		keywords: ['sites with highest data usage', 'highest data usage', 'top data usage'],
		postNavAction: 'expand-widget:Sites with Highest Data Usage',
	},
	{
		id: 'dash-sites-least-data',
		path: '/Dashboard',
		label: 'Home Dashboard > Sites with Least Data Usage',
		section: 'Home Dashboard',
		description: 'Open the Sites with Least Data Usage graph.',
		keywords: ['sites with least data usage', 'least data usage', 'lowest data usage'],
		postNavAction: 'expand-widget:Sites with Least Data Usage',
	},
	{
		id: 'dash-sites-max-critical',
		path: '/Dashboard',
		label: 'Home Dashboard > Sites with Max Critical alarms',
		section: 'Home Dashboard',
		description: 'Open the Sites with Max Critical alarms graph.',
		keywords: [
			'sites with max critical alarms', 'max critical alarms',
			'highest critical alarms', 'most critical alarms',
		],
		postNavAction: 'expand-widget:Sites with Max Critical alarms',
	},
	{
		id: 'dash-sites-least-critical',
		path: '/Dashboard',
		label: 'Home Dashboard > Sites with Least Critical alarms',
		section: 'Home Dashboard',
		description: 'Open the Sites with Least Critical alarms graph.',
		keywords: [
			'sites with least critical alarms', 'least critical alarms',
			'lowest critical alarms', 'fewest critical alarms',
		],
		postNavAction: 'expand-widget:Sites with Least Critical alarms',
	},
	{
		id: 'dash-ssid-data-usage',
		path: '/Dashboard',
		label: 'Home Dashboard > Data usage on SSID (GB)',
		section: 'Home Dashboard',
		description: 'Open the Data usage on SSID graph.',
		keywords: ['data usage on ssid', 'ssid data usage', 'ssid traffic'],
		postNavAction: 'expand-widget:Data usage on SSID (GB)',
	},
	{
		id: 'dash-ap-uptime',
		path: '/Dashboard',
		label: 'Home Dashboard > AP Uptime',
		section: 'Home Dashboard',
		description: 'Open the AP Uptime graph.',
		keywords: ['ap uptime', 'access point uptime'],
		postNavAction: 'expand-widget:AP Uptime',
	},
	{
		id: 'dash-ap-models-firmware',
		path: '/Dashboard',
		label: 'Home Dashboard > AP Models & Firmwares',
		section: 'Home Dashboard',
		description: 'Open the AP Models & Firmwares graph.',
		keywords: ['ap models firmwares', 'ap model firmware', 'ap firmware distribution'],
		postNavAction: 'expand-widget:AP Models & Firmwares(Now)',
	},
	{
		id: 'dash-switch-models-firmware',
		path: '/Dashboard',
		label: 'Home Dashboard > Switch Models & Firmwares',
		section: 'Home Dashboard',
		description: 'Open the Switch Models & Firmwares graph.',
		keywords: ['switch models firmwares', 'switch model firmware', 'switch firmware distribution'],
		postNavAction: 'expand-widget:Switch Models & Firmwares(Now)',
	},
	{
		id: 'dash-vendors',
		path: '/Dashboard',
		label: 'Home Dashboard > Vendors',
		section: 'Home Dashboard',
		description: 'Open the Vendors graph.',
		keywords: ['vendors graph', 'vendors widget', 'ap vendors'],
		postNavAction: 'expand-widget:Vendors',
	},

	// ----- Home Dashboard bottom accordions -----
	// These hints open one of the data accordions on Home Dashboard.
	// `expand-widget:<title>` both scrolls to and toggles open the accordion,
	// using hfclScrollToWidget under the hood — so a single intent is enough
	// to bring the user to the data they asked for.
	//
	// Keyword design: each accordion accepts the bare noun ("sites",
	// "switches", "access points"), the "all X" form ("all sites"), and
	// common verbs ("open / show / view / see / list / go to X"). This
	// catches the natural phrasings users actually type without relying on
	// Ollama's reasoning. The earlier dedicated `<thing>-dashboard` hints
	// have been removed — they pointed at `?section=X` query strings that
	// nothing on the dashboard reads.
	{
		id: 'dash-all-sites',
		path: '/Dashboard',
		label: 'Home Dashboard > All Sites',
		section: 'Home Dashboard',
		description: 'Open the All Sites accordion on Home Dashboard.',
		keywords: [
			'all sites', 'sites', 'site list', 'all sites list',
			'open sites', 'open all sites', 'open the sites',
			'show sites', 'show all sites', 'show me sites', 'show me the sites',
			'view sites', 'view all sites', 'see sites', 'see all sites',
			'list sites', 'go to sites', 'navigate to sites',
			'sites accordion', 'all sites accordion', 'all sites table',
			'site details', 'sites dashboard', 'site data table',
		],
		postNavAction: 'expand-widget:All Sites',
	},
	{
		id: 'dash-add-site',
		path: '/Dashboard',
		label: 'Home Dashboard > All Sites (Add Site)',
		section: 'Home Dashboard',
		description: 'Open the All Sites accordion and bring up the Add Site form.',
		keywords: [
			'add site', 'add a site', 'add new site', 'create site', 'create a site',
			'new site', 'register site', 'add the site', 'please add site',
		],
		postNavAction: ['expand-widget:All Sites', 'dash-add-site'],
	},
	{
		id: 'dash-all-global-ssids',
		path: '/Dashboard',
		label: 'Home Dashboard > Global SSIDs',
		section: 'Home Dashboard',
		description: 'Open the Global SSIDs accordion on Home Dashboard.',
		keywords: [
			'global ssids', 'global ssid', 'all ssids', 'ssids', 'ssid list',
			'open ssids', 'open global ssids', 'open all ssids',
			'show ssids', 'show global ssids', 'show all ssids', 'show me ssids',
			'view ssids', 'view global ssids', 'see ssids', 'see global ssids',
			'list ssids', 'go to ssids', 'navigate to ssids',
			'ssids accordion', 'global ssids accordion', 'all global ssids',
			'ssid details', 'global ssid dashboard', 'global ssid data table',
		],
		postNavAction: 'expand-widget:Global SSIDs',
	},
	{
		id: 'dash-add-ssid',
		path: '/Dashboard',
		label: 'Home Dashboard > Global SSIDs (Add SSID)',
		section: 'Home Dashboard',
		description: 'Open the Global SSIDs accordion and bring up the Add SSID form.',
		keywords: [
			'add ssid', 'add a ssid', 'add new ssid', 'create ssid', 'create a ssid',
			'add global ssid', 'create global ssid', 'new ssid', 'new wifi network',
			'please add ssid', 'add the ssid',
		],
		postNavAction: ['expand-widget:Global SSIDs', 'dash-add-ssid'],
	},
	{
		id: 'dash-all-access-points',
		path: '/Dashboard',
		label: 'Home Dashboard > All Access Points',
		section: 'Home Dashboard',
		description: 'Open the All Access Points accordion on Home Dashboard.',
		keywords: [
			'all access points', 'access points', 'access point list',
			'access point', 'aps', 'ap list',
			'open access points', 'open access point', 'open all access points',
			'open the access points', 'open aps',
			'show access points', 'show access point', 'show all access points',
			'show me access points', 'show me the access points', 'show aps',
			'view access points', 'view all access points', 'see access points',
			'see all access points', 'list access points', 'list aps',
			'go to access points', 'go to aps', 'navigate to access points',
			'access points accordion', 'all access points accordion',
			'all access points table', 'access point details', 'ap details',
			'access point dashboard', 'ap dashboard',
		],
		postNavAction: 'expand-widget:All Access Points',
	},
	{
		id: 'dash-add-access-point',
		path: '/Dashboard',
		label: 'Home Dashboard > All Access Points (Add Access Point)',
		section: 'Home Dashboard',
		description: 'Open the All Access Points accordion and bring up the Add Access Point form.',
		keywords: [
			'add access point', 'add a access point', 'add an access point',
			'add new access point', 'add access points', 'create access point',
			'add ap', 'add a ap', 'add the ap', 'add the access point',
			'add the access points', 'please add the access points',
			'please add access point', 'register ap', 'register access point',
			'new ap',
		],
		postNavAction: ['expand-widget:All Access Points', 'dash-add-access-point'],
	},
	{
		id: 'dash-all-switches',
		path: '/Dashboard',
		label: 'Home Dashboard > All Switches',
		section: 'Home Dashboard',
		description: 'Open the All Switches accordion on Home Dashboard.',
		keywords: [
			'all switches', 'switches', 'switch list', 'switch',
			'open switches', 'open switch', 'open all switches', 'open the switches',
			'show switches', 'show all switches', 'show me switches',
			'show me the switches', 'view switches', 'view all switches',
			'see switches', 'see all switches', 'list switches',
			'go to switches', 'navigate to switches',
			'switches accordion', 'all switches accordion', 'all switches table',
			'switch details', 'switch dashboard', 'switches dashboard',
			'switch data table',
		],
		postNavAction: 'expand-widget:All Switches',
	},
	{
		id: 'dash-add-switch',
		path: '/Dashboard',
		label: 'Home Dashboard > All Switches (Add Switch)',
		section: 'Home Dashboard',
		description: 'Open the All Switches accordion and bring up the Add Switch form.',
		keywords: [
			'add switch', 'add a switch', 'add new switch', 'create switch',
			'add the switch', 'add switches', 'add the switches',
			'please add switch', 'please add the switches', 'register switch',
			'new switch',
		],
		postNavAction: ['expand-widget:All Switches', 'dash-add-switch'],
	},
	{
		id: 'dash-all-clients',
		path: '/Dashboard',
		label: 'Home Dashboard > All Clients',
		section: 'Home Dashboard',
		description: 'Open the All Clients accordion on Home Dashboard.',
		keywords: [
			'all clients', 'clients', 'client list', 'connected clients list',
			'open clients', 'open all clients', 'open the clients',
			'show clients', 'show all clients', 'show me clients',
			'show me the clients', 'view clients', 'view all clients',
			'see clients', 'see all clients', 'list clients',
			'go to clients', 'navigate to clients',
			'clients accordion', 'all clients accordion', 'all clients table',
			'client details', 'client dashboard',
		],
		postNavAction: 'expand-widget:All Clients',
	},
];

/* ----- helpers ----- */

const normalize = (text = '') => String(text).toLowerCase().trim();

/**
 * Strip punctuation and split into lowercase word tokens.
 *
 * @param {string} text
 * @return {string[]}
 */
const tokenize = (text = '') =>
	String(text)
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, ' ')
		.split(' ')
		.filter(Boolean);

/**
 * Damerau-Levenshtein edit distance — like Levenshtein but counts an
 * adjacent-character transposition (e.g. "bluk" -> "bulk", "teh" -> "the")
 * as a single edit instead of two. Catches the most common kind of typo
 * humans actually make.
 *
 * @param {string} a
 * @param {string} b
 * @return {number}
 */
const levenshtein = (a, b) => {
	if (a === b) return 0;
	if (!a.length) return b.length;
	if (!b.length) return a.length;
	const m = a.length;
	const n = b.length;
	// Need three rows for Damerau (curr, prev, prev-prev). Use full matrix
	// for clarity — keywords are short so memory is irrelevant.
	const d = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
	for (let i = 0; i <= m; i += 1) d[i][0] = i;
	for (let j = 0; j <= n; j += 1) d[0][j] = j;
	for (let i = 1; i <= m; i += 1) {
		for (let j = 1; j <= n; j += 1) {
			const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
			d[i][j] = Math.min(
				d[i - 1][j] + 1,        // delete
				d[i][j - 1] + 1,        // insert
				d[i - 1][j - 1] + cost  // substitute
			);
			// Adjacent-character transposition.
			if (
				i > 1 &&
				j > 1 &&
				a.charCodeAt(i - 1) === b.charCodeAt(j - 2) &&
				a.charCodeAt(i - 2) === b.charCodeAt(j - 1)
			) {
				d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
			}
		}
	}
	return d[m][n];
};

/**
 * Per-token typo budget — longer words tolerate more typos. 1 typo per ~4
 * characters. Very short words (<= 3 chars) must match exactly.
 *
 * @param {string} word
 * @return {number}
 */
const allowedEditsFor = (word) => {
	if (word.length <= 3) return 0;
	if (word.length <= 6) return 1;
	return 2;
};

/**
 * Returns true if any message token is "close enough" to the keyword token —
 * either it contains the keyword token, the keyword token contains it, or
 * the edit distance is within the typo budget.
 *
 * @param {string} kwToken
 * @param {string[]} msgTokens
 * @return {boolean}
 */
const tokenMatchesAny = (kwToken, msgTokens) => {
	const budget = allowedEditsFor(kwToken);
	for (const t of msgTokens) {
		if (t === kwToken) return true;
		// Substring match — require BOTH sides to be at least 4 chars, otherwise
		// short message tokens like "i", "to", "do" match every long keyword
		// that happens to contain those letters.
		if (
			kwToken.length >= 4 &&
			t.length >= 4 &&
			(t.includes(kwToken) || kwToken.includes(t))
		) {
			return true;
		}
		if (Math.abs(t.length - kwToken.length) <= budget && levenshtein(t, kwToken) <= budget) {
			return true;
		}
	}
	return false;
};

/**
 * Returns true if EVERY token in the keyword phrase has a close-enough match
 * somewhere in the message. So "user management form" matches
 * "where do I get user menagment form" because each of {user, management,
 * form} finds a fuzzy hit in the message.
 *
 * @param {string} keyword
 * @param {string[]} msgTokens
 * @return {boolean}
 */
const keywordMatches = (keyword, msgTokens) => {
	const kwTokens = tokenize(keyword);
	if (!kwTokens.length) return false;
	return kwTokens.every((kt) => tokenMatchesAny(kt, msgTokens));
};

/**
 * True if any keyword of any hint matches the message tokens.
 *
 * @param {string[]} msgTokens
 * @return {boolean}
 */
const anyHintMatches = (msgTokens) =>
	HFCL_NAVIGATION_HINTS.some((item) =>
		item.keywords.some((kw) => keywordMatches(kw, msgTokens))
	);

/**
 * Returns true when the message looks like something the chat can handle —
 * either it has navigation phrasing (where / how do I open / take me to /
 * etc.), or it contains action verbs (change / create / add / open / update),
 * or it strongly matches at least one multi-word keyword from the registry.
 *
 * @param {string} q - user message
 * @return {boolean}
 */
export const isNavigationQuestion = (q) => {
	if (!q) return false;
	const lower = normalize(q);
	const tokens = tokenize(lower);

	const hasNavPhrasing =
		/\b(where|which)\b/.test(lower) ||
		/\bhow\s+(?:do\s+i\s+|can\s+i\s+|to\s+)?(open|go|navigate|find|get|see|reach)\b/.test(lower) ||
		// "take me to X", "take me X", "navigate to X", "go to X", "open the X",
		// "open X", "show me X", "bring me to X", "switch to X"
		/\b(take\s+me|bring\s+me|navigate(\s+to)?|go\s+to|open(\s+the)?|show\s+me|switch\s+to|jump\s+to|move\s+to)\b/.test(lower) ||
		// "can you take/show/open ..."
		/\bcan\s+you\s+(take|show|open|navigate|go|find|reach|jump|switch|bring|help)\b/.test(lower) ||
		/\b(route|path|link)\s+(to|for)\b/.test(lower) ||
		/\bwhich\s+(page|screen|section|menu|tab|form)\b/.test(lower);

	const hasActionVerb =
		/\b(change|create|add|update|reset|delete|remove|edit|manage|view|see|find|get|invite|configure|setup|set\s+up)\b/.test(lower);

	// A "strong" keyword match means a multi-token keyword whose every token
	// has a fuzzy hit in the message. That alone is enough to treat the
	// message as actionable, even without nav phrasing or action verbs.
	const hasStrongKeyword = HFCL_NAVIGATION_HINTS.some((item) =>
		item.keywords.some((kw) => {
			const kwTokens = tokenize(kw);
			return kwTokens.length >= 2 && kwTokens.every((kt) => tokenMatchesAny(kt, tokens));
		})
	);

	// A "bare keyword" message — the whole input is essentially just one
	// registered keyword, e.g. "nhc", "logs", "backup", "bulk operations".
	// Accept this even without nav phrasing or action verb.
	const isBareKeyword = tokens.length > 0 && tokens.length <= 3 &&
		HFCL_NAVIGATION_HINTS.some((item) =>
			item.keywords.some((kw) => {
				const kwTokens = tokenize(kw);
				if (kwTokens.length === 0) return false;
				// every keyword token has a fuzzy hit AND every message token
				// is itself part of the keyword (no extra random words)
				const everyKwTokenHits = kwTokens.every((kt) => tokenMatchesAny(kt, tokens));
				const everyMsgTokenUsed = tokens.every((mt) =>
					kwTokens.some((kt) => tokenMatchesAny(mt, [kt]))
				);
				return everyKwTokenHits && everyMsgTokenUsed;
			})
		);

	if (!hasNavPhrasing && !hasActionVerb && !hasStrongKeyword && !isBareKeyword) return false;
	return anyHintMatches(tokens);
};

/**
 * Direct intent match — returns the hint whose longest keyword matches the
 * message tokens (with typo tolerance). Used after `isNavigationQuestion`
 * has confirmed it's a nav query.
 *
 * @param {string} q - user message
 * @return {Object|undefined} matched hint
 */
export const findHfclNavigationHint = (q) => {
	const top = findTopHfclNavigationHints(q, { limit: 1 });
	return top[0]?.hint || undefined;
};

/**
 * Ranked candidate matcher. Returns up to `limit` hints with their best
 * matching-keyword score, sorted descending. Used by the chat to detect
 * ambiguous queries — if the runner-up's score is close to the winner's,
 * we surface both to the user as disambiguation buttons.
 *
 * The score for a hint is the maximum of `keyword.length * 10 + tokenCount`
 * across all of its keywords that fully match the message tokens. We keep
 * one entry per hint id (so a hint with many close keywords doesn't
 * crowd out everything else).
 *
 * @param {string} q user message
 * @param {Object} [opts] options bag
 * @param {number} [opts.limit] max number of candidates to return (default 3)
 * @return {Array} array of { hint, score, keyword } sorted desc by score
 */
export const findTopHfclNavigationHints = (q, opts = {}) => {
	const limit = Math.max(1, opts.limit || 3);
	const msgTokens = tokenize(normalize(q));
	if (!msgTokens.length) return [];

	const perHintBest = new Map(); // id -> { hint, score, keyword }
	for (const hint of HFCL_NAVIGATION_HINTS) {
		let bestForHint = null;
		for (const kw of hint.keywords) {
			if (!keywordMatches(kw, msgTokens)) continue;
			const score = kw.length * 10 + tokenize(kw).length;
			if (!bestForHint || score > bestForHint.score) {
				bestForHint = { hint, score, keyword: kw };
			}
		}
		if (bestForHint) perHintBest.set(hint.id, bestForHint);
	}

	return Array.from(perHintBest.values())
		.sort((a, b) => b.score - a.score)
		.slice(0, limit);
};

/**
 * Inspect the result of `findTopHfclNavigationHints` and decide whether
 * the matches are "ambiguous" (i.e. the runner-up is close enough to the
 * winner that we should ask the user which one they meant).
 *
 * Heuristic: ambiguous when there are >= 2 candidates AND the runner-up's
 * score is at least `minRatio` of the winner's. Default 0.78 — empirically
 * separates obvious dominators (e.g. "open access points" → all-access-
 * points at 183 vs nothing else at 0) from close calls (e.g. "switch" alone
 * could be the switches accordion or the switches-detail page).
 *
 * @param {Array} candidates  result of findTopHfclNavigationHints
 * @param {number} [minRatio]
 * @return {boolean}
 */
export const isAmbiguous = (candidates, minRatio = 0.92) => {
	if (!Array.isArray(candidates) || candidates.length < 2) return false;
	const [first, second] = candidates;
	if (!first?.score || !second?.score) return false;
	// Same hint id appearing twice (defensive — shouldn't happen with the
	// per-hint dedupe above).
	if (first.hint.id === second.hint.id) return false;
	return second.score >= first.score * minRatio;
};

/**
 * Look up a hint by its `id`. Used by the chat hook to convert a backend
 * classifier reply (which returns only the intent id) into a full hint
 * object with path / label / postNavAction.
 *
 * @param {string} id
 * @return {Object|undefined}
 */
export const findHintById = (id) => {
	if (!id) return undefined;
	return HFCL_NAVIGATION_HINTS.find((item) => item.id === id);
};

/**
 * Affirmative reply detector — used ONLY when there is a pending offer
 * and we need to know if the user replied "yes" (versus typing a new
 * command that happens to start with a verb).
 *
 * CRITICAL: each pattern must match the *entire* normalised input. The
 * old regex anchored only at the start (`^(yes|...|open|...)`) which
 * meant any message starting with "open" — like "open access points" —
 * was treated as Yes. We now require the whole input to be an
 * affirmative phrase, so command-style queries never get mis-classified.
 *
 * @param {string} q
 * @return {boolean}
 */
export const isAffirmative = (q) => {
	const t = normalize(q).replace(/[?.!,]+$/, '').trim();
	if (!t) return false;
	// Single-token affirmatives — must be the whole message.
	if (/^(yes|y|yeah|yep|yup|ok|okay|sure|proceed|fine|alright|cool|great|absolutely|definitely)$/i.test(t)) {
		return true;
	}
	// Multi-word affirmative phrases — must be the whole message.
	if (/^(yes\s+please|yes\s+proceed|please\s+proceed|please\s+do|do\s+it|go\s+ahead|sounds\s+good|sure\s+thing|let'?s\s+do\s+(?:it|that)|that\s+works|works\s+for\s+me|let'?s\s+go|do\s+that|please\s+go\s+ahead)$/i.test(t)) {
		return true;
	}
	return false;
};

/**
 * Negative reply detector. Same anchoring rule as isAffirmative — must
 * match the entire input so "no problem with that" doesn't get treated
 * as a "no" reply.
 *
 * @param {string} q
 * @return {boolean}
 */
export const isNegative = (q) => {
	const t = normalize(q).replace(/[?.!,]+$/, '').trim();
	if (!t) return false;
	if (/^(no|n|nope|nah|nay|cancel|stop|abort)$/i.test(t)) return true;
	if (/^(no\s+thanks?|not\s+now|maybe\s+later|don'?t|do\s+not|never\s+mind|nevermind|forget\s+it|skip\s+(?:it|that))$/i.test(t)) {
		return true;
	}
	return false;
};
